// What a formula works with while it is worked out: single values, blocks of cells read from a tab, and arrays
// ({1,2;3,4}, or what UNIQUE and SORT give). The rules for turning one into another follow Google Sheets: an empty
// cell is 0 in sums and "" in text, text that reads as a number counts as one in arithmetic, and comparisons put
// numbers before text before TRUE and FALSE.

import { err, isErr, textNumber, type Scalar, SheetErr } from "./values";

/** A tab as a formula reads it: its cells' values, and how far its cells in use go. */
export interface TabView {
  id: string;
  name: string;
  get(r: number, c: number): Scalar;
  /** rows and columns in use: the last used row's index plus one */
  rows: number;
  cols: number;
}

/** A block of cells a reference names, read only when a function asks. */
export class RangeV {
  constructor(
    public tab: TabView,
    public r0: number,
    public c0: number,
    public r1: number,
    public c1: number,
  ) {}
  /** its last row and column, a whole column or row cut to the cells in use */
  get er(): number {
    return Math.max(this.r0, Math.min(this.r1, Math.max(this.tab.rows - 1, this.r0)));
  }
  get ec(): number {
    return Math.max(this.c0, Math.min(this.c1, Math.max(this.tab.cols - 1, this.c0)));
  }
  get h(): number {
    return this.er - this.r0 + 1;
  }
  get w(): number {
    return this.ec - this.c0 + 1;
  }
  at(i: number, j: number): Scalar {
    return this.tab.get(this.r0 + i, this.c0 + j);
  }
}

export class Arr {
  /** rows: its values; from: when each row is a whole row of a block (FILTER, SORT, UNIQUE), the block and which of its
   *  rows each is, so the spilled cells take the look of the cells they came from (a date stays a date) */
  constructor(
    public rows: Scalar[][],
    public from?: { range: RangeV; rows: number[] },
  ) {}
  get h(): number {
    return this.rows.length;
  }
  get w(): number {
    return this.rows[0]?.length ?? 0;
  }
  at(i: number, j: number): Scalar {
    return this.rows[i]?.[j] ?? null;
  }
}

export type Val = Scalar | RangeV | Arr;

export const isBlock = (v: Val): v is RangeV | Arr => v instanceof RangeV || v instanceof Arr;

/** How much a formula may read before it stops with #VALUE!, so no sheet ever hangs the page. */
export class Budget {
  used = 0;
  constructor(public max = 20_000_000) {}
  spend(n: number) {
    this.used += n;
    if (this.used > this.max) throw new SheetErr("#VALUE!", "This formula reads too many cells to work out.");
  }
}

/** The first cell of a block, as a single value; a value as it is. */
export function scalar(v: Val): Scalar {
  if (v instanceof RangeV || v instanceof Arr) return v.h >= 1 && v.w >= 1 ? v.at(0, 0) : null;
  return v;
}

/** A value as a number, the way arithmetic takes it. */
export function toNum(v: Scalar, dmy = false): number | SheetErr {
  if (v === null) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (isErr(v)) return v;
  if (v.trim() === "") return 0;
  const n = textNumber(v, dmy);
  return n === null ? err("#VALUE!", `"${v.length > 30 ? v.slice(0, 30) + "…" : v}" is text, not a number.`) : n;
}

/** A number as text, the way & and the text functions show it: as an automatic format would, without separators. */
export function numText(n: number): string {
  if (Number.isInteger(n) && Math.abs(n) < 1e21) return String(n);
  const s = n.toPrecision(15);
  return s.includes("e") ? String(Number(s)).toUpperCase() : s.replace(/\.?0+$/, "");
}

export function toStr(v: Scalar): string | SheetErr {
  if (v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return numText(v);
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return v;
}

export function toBool(v: Scalar): boolean | SheetErr {
  if (v === null) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (isErr(v)) return v;
  const u = v.trim().toUpperCase();
  if (u === "TRUE") return true;
  if (u === "FALSE" || u === "") return false;
  return err("#VALUE!", `"${v}" is not TRUE or FALSE.`);
}

/**
 * A number to 15 significant digits, as Google Sheets and Excel keep and compare numbers: 0.1+0.2 is 0.3 there, where
 * the computer's own sum is 0.30000000000000004. Comparisons, lookups and conditions all go through it.
 */
export function sig15(n: number): number {
  if (n === 0 || !Number.isFinite(n)) return n;
  return Number(n.toPrecision(15));
}

const RANK = (v: Scalar) => (typeof v === "number" ? 0 : typeof v === "string" ? 1 : typeof v === "boolean" ? 2 : 3);

/** Two values in a spreadsheet's order: numbers, then text (case aside), then FALSE and TRUE; empty as the other's kind. */
export function cmp(a: Scalar, b: Scalar): number {
  if (a === null) a = typeof b === "string" ? "" : typeof b === "boolean" ? false : 0;
  if (b === null) b = typeof a === "string" ? "" : typeof a === "boolean" ? false : 0;
  const ra = RANK(a);
  const rb = RANK(b);
  if (ra !== rb) return ra - rb;
  if (typeof a === "number" && typeof b === "number") {
    const x = sig15(a);
    const y = sig15(b);
    return x < y ? -1 : x > y ? 1 : 0;
  }
  if (typeof a === "string" && typeof b === "string") {
    const x = a.toLowerCase();
    const y = b.toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  }
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return 0;
}

/** Each value in a value or block, row by row, with whether it came from a block (a sum skips text in one). */
export function* each(v: Val, budget?: Budget): Generator<[Scalar, boolean]> {
  if (v instanceof RangeV || v instanceof Arr) {
    const h = v.h;
    const w = v.w;
    budget?.spend(h * w);
    for (let i = 0; i < h; i++) for (let j = 0; j < w; j++) yield [v.at(i, j), true];
  } else yield [v, false];
}

/** A block's values as rows; a single value as one row of one. */
export function grid(v: Val, budget?: Budget): Scalar[][] {
  if (v instanceof Arr) return v.rows;
  if (v instanceof RangeV) {
    budget?.spend(v.h * v.w);
    const out: Scalar[][] = [];
    for (let i = 0; i < v.h; i++) {
      const row: Scalar[] = [];
      for (let j = 0; j < v.w; j++) row.push(v.at(i, j));
      out.push(row);
    }
    return out;
  }
  return [[v]];
}

export function dims(v: Val): { h: number; w: number } {
  return v instanceof RangeV || v instanceof Arr ? { h: v.h, w: v.w } : { h: 1, w: 1 };
}

export function atIn(v: Val, i: number, j: number): Scalar {
  if (v instanceof RangeV || v instanceof Arr) return i < v.h && j < v.w ? v.at(i, j) : null;
  return i === 0 && j === 0 ? v : null;
}

// ---------------------------------------------------------------------------
// criteria, as SUMIF and COUNTIF take them: ">5", "<>done", "apple*", 10, ""

export type Crit = (v: Scalar) => boolean;

export function wildcard(pattern: string): RegExp {
  let re = "";
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i];
    if (ch === "~" && i + 1 < pattern.length) re += pattern[++i].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    else if (ch === "*") re += "[\\s\\S]*";
    else if (ch === "?") re += "[\\s\\S]";
    else re += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  return new RegExp(`^${re}$`, "i");
}

export function criterion(c: Scalar, dmy = false): Crit {
  if (isErr(c)) return (v) => isErr(v) && v.code === c.code;
  if (typeof c === "number") {
    const k = sig15(c);
    return (v) => (typeof v === "number" ? sig15(v) === k : typeof v === "string" ? ((n) => n !== null && sig15(n) === k)(textNumber(v, dmy)) : false);
  }
  if (typeof c === "boolean") return (v) => v === c;
  if (c === null) return (v) => v === null || v === "";
  const m = /^(<=|>=|<>|<|>|=)?([\s\S]*)$/.exec(c)!;
  const op = m[1] ?? "=";
  const rest = m[2];
  const n0 = rest.trim() === "" ? null : textNumber(rest, dmy);
  const n = n0 === null ? null : sig15(n0);
  if (rest === "") {
    if (op === "=") return (v) => v === null || v === "";
    if (op === "<>") return (v) => v !== null && v !== "";
  }
  if (n !== null) {
    const test = (x: number) => (op === "=" ? x === n : op === "<>" ? x !== n : op === "<" ? x < n : op === ">" ? x > n : op === "<=" ? x <= n : x >= n);
    return (v) => {
      if (typeof v === "number") return test(sig15(v));
      if (op === "<>") return true;
      return false;
    };
  }
  const up = rest.toUpperCase();
  if (up === "TRUE" || up === "FALSE") {
    const b = up === "TRUE";
    return (v) => (op === "<>" ? v !== b : v === b);
  }
  if (op === "=" || op === "<>") {
    const re = wildcard(rest);
    return (v) => {
      const hit = typeof v === "string" ? re.test(v) : false;
      return op === "=" ? hit : !hit;
    };
  }
  return (v) => {
    if (typeof v !== "string") return false;
    const d = cmp(v, rest);
    return op === "<" ? d < 0 : op === ">" ? d > 0 : op === "<=" ? d <= 0 : d >= 0;
  };
}

/** Whether two values are the same for a lookup: text by its letters (case aside, wildcards when asked), numbers by value. */
export function same(a: Scalar, key: Scalar, wild = false): boolean {
  if (typeof key === "string" && typeof a === "string") {
    if (wild && /[*?~]/.test(key)) return wildcard(key).test(a);
    return a.toLowerCase() === key.toLowerCase();
  }
  if (a === null || key === null) return (a === null || a === "") && (key === null || key === "");
  return typeof a === typeof key && cmp(a, key) === 0;
}
